import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { API_URL } from './config';
import './Equity.css';
import './Margin.css';

function formatCurrency(value, currency) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
  return `${symbol}${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPct(value, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(digits)}%`;
}

function formatX(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(digits)}x`;
}

function formatUpdatedAt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const minutesAgo = Math.round((now - d) / 60000);
  const stamp = d.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
  if (minutesAgo < 0) return stamp;
  if (minutesAgo < 60) return `${stamp} (${minutesAgo}m ago)`;
  const hoursAgo = Math.round(minutesAgo / 60);
  return `${stamp} (${hoursAgo}h ago)`;
}

/** A snapshot older than this is flagged stale - TWS was probably down for
 * this account (hourly refresh, so anything past a couple of missed runs
 * is worth calling out rather than silently showing an old number). */
const STALE_AFTER_MINUTES = 150;

function isStale(iso) {
  if (!iso) return true;
  const minutesAgo = (new Date() - new Date(iso)) / 60000;
  return minutesAgo > STALE_AFTER_MINUTES;
}

/** Threshold for showing the manual "Refresh Now" button - much tighter
 * than STALE_AFTER_MINUTES above (which flags "TWS was probably down").
 * This one just means "more than a few minutes since the hourly cron ran
 * or someone last refreshed manually", so the button is worth offering. */
const REFRESH_BUTTON_AFTER_MINUTES = 5;

function needsRefreshButton(iso) {
  if (!iso) return true;
  const minutesAgo = (new Date() - new Date(iso)) / 60000;
  return minutesAgo > REFRESH_BUTTON_AFTER_MINUTES;
}

/* ------------------------------------------------------------------ *
 * Risk bands - "Moderate" profile (confirmed 2026-09-01): balances
 * flagging real risk against not crying wolf on ordinary margin use.
 * Cushion: higher is safer. Margin used / leverage: higher is riskier.
 * ------------------------------------------------------------------ */

const BANDS = {
  cushion: { green: 40, orange: 20, higherIsSafer: true },
  marginUsed: { orange: 40, red: 70, higherIsSafer: false },
  leverage: { orange: 1.5, red: 3, higherIsSafer: false },
};

const BAND_LABEL = { green: 'Comfortable', orange: 'Watch', red: 'Danger' };

// The leverage bar's full width represents 0x up to this multiple of the red
// threshold, so the red zone still has visible width instead of ending at
// the bar's edge (e.g. red=3x -> bar shows 0x to 4.5x).
const LEVERAGE_BAR_MAX = BANDS.leverage.red * 1.5;

function cushionBand(pct) {
  if (pct >= BANDS.cushion.green) return 'green';
  if (pct >= BANDS.cushion.orange) return 'orange';
  return 'red';
}

function marginUsedBand(pct) {
  if (pct < BANDS.marginUsed.orange) return 'green';
  if (pct < BANDS.marginUsed.red) return 'orange';
  return 'red';
}

function leverageBand(x) {
  if (x < BANDS.leverage.orange) return 'green';
  if (x < BANDS.leverage.red) return 'orange';
  return 'red';
}

/* ------------------------------------------------------------------ *
 * Small click-to-toggle explainer popover (per-metric), same pattern
 * as TWR.js's InfoPopover - for when a native `title` tooltip is too
 * cramped for the amount of text.
 * ------------------------------------------------------------------ */
function InfoPopover({ children, align = 'left' }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="margin-info-btn"
        onClick={() => setOpen((o) => !o)}
        title="What is this?"
        aria-label="Explanation"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke={open ? '#667eea' : '#9ca3af'} strokeWidth="2" />
          <path d="M12 11V16.5" stroke={open ? '#667eea' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="7.5" r="1.25" fill={open ? '#667eea' : '#9ca3af'} />
        </svg>
      </button>
      {open ? (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
          <div
            className="margin-info-popover"
            style={{ position: 'absolute', top: '1.6rem', [align]: 0 }}
          >
            {children}
          </div>
        </>
      ) : null}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * General "What matters here" explainer, bigger modal - same pattern
 * as AdvancedOptionsDashboard's info-modal.
 * ------------------------------------------------------------------ */
function WhatMattersModal({ onClose }) {
  return (
    <div className="margin-modal-overlay" onClick={onClose}>
      <div className="margin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="margin-modal-header">
          <h2>What matters on this page</h2>
          <button className="margin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="margin-modal-content">
          <p>
            <strong>Cushion</strong> is the headline number. It's IBKR's own measure of how far
            you are from a margin call: <em>Excess Liquidity ÷ Net Liquidation</em>. At 0%, you're
            sitting exactly at your maintenance margin requirement - any further loss (or margin
            requirement increase) can trigger a forced liquidation. Negative cushion means that
            has already started.
          </p>
          <p>
            <strong>Margin used</strong> shows how much of your account's total value is currently
            tied up as a maintenance-margin requirement for your open positions. Low = plenty of
            room; high = little room before requirements alone eat your excess liquidity.
          </p>
          <p>
            <strong>Leverage</strong> is your total position value (Gross Position Value) divided
            by Net Liquidation. 1.0x means you're fully cash-covered - no borrowing. Above 1.0x
            means you're controlling more market exposure than your own capital, which amplifies
            both gains and losses (and margin risk) in the same direction.
          </p>
          <p>
            The colour bands (green / orange / red) reflect a <strong>Moderate</strong> risk
            profile - room to use margin, but flagging it well before things get genuinely tight.
            They're a guide, not a guarantee: IBKR can raise margin requirements on short notice
            (e.g. around earnings or high volatility), which can move you into a worse band even
            if you haven't traded.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Semi-circular risk gauge (SVG). value: 0-100 (clamped for the arc
 * position; the real number - which can go negative - is always shown
 * as text regardless of clamping).
 * ------------------------------------------------------------------ */
function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

function arcPath(cx, cy, r, valueStart, valueEnd) {
  const angleStart = 180 - (valueStart / 100) * 180;
  const angleEnd = 180 - (valueEnd / 100) * 180;
  const p1 = polarToCartesian(cx, cy, r, angleStart);
  const p2 = polarToCartesian(cx, cy, r, angleEnd);
  const largeArc = angleStart - angleEnd > 180 ? 1 : 0;
  return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y}`;
}

const ZONE_COLOR = { red: '#dc2626', orange: '#d97706', green: '#059669' };

function RiskGauge({ pctValue }) {
  const cx = 100;
  const cy = 92;
  const r = 76;
  const clamped = Math.max(0, Math.min(100, pctValue));
  const band = cushionBand(pctValue);
  const needleAngle = 180 - (clamped / 100) * 180;
  const needleTip = polarToCartesian(cx, cy, r - 20, needleAngle);
  const textColor = ZONE_COLOR[band];

  // zone boundaries from BANDS.cushion: red [0,20), orange [20,40), green [40,100]
  const zones = [
    { from: 0, to: BANDS.cushion.orange, color: ZONE_COLOR.red },
    { from: BANDS.cushion.orange, to: BANDS.cushion.green, color: ZONE_COLOR.orange },
    { from: BANDS.cushion.green, to: 100, color: ZONE_COLOR.green },
  ];

  return (
    <div className="margin-gauge-wrap">
      <svg width="200" height="140" viewBox="0 0 200 140">
        {zones.map((z) => (
          <path
            key={z.from}
            d={arcPath(cx, cy, r, z.from, z.to)}
            fill="none"
            stroke={z.color}
            strokeWidth="13"
            strokeOpacity="0.9"
          />
        ))}
        <line
          x1={cx}
          y1={cy}
          x2={needleTip.x}
          y2={needleTip.y}
          stroke="#1a1a1a"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r="5" fill="#1a1a1a" />
        {/* Readout sits below the pivot, well clear of the arc and needle */}
        <text x={cx} y={cy + 32} textAnchor="middle" fontSize="26" fontWeight="700" fill={textColor}>
          {formatPct(pctValue, 0)}
        </text>
        <text x={cx} y={cy + 48} textAnchor="middle" fontSize="11" fontWeight="600" letterSpacing="0.05em" fill={textColor}>
          {BAND_LABEL[band].toUpperCase()}
        </text>
      </svg>
      <div className="margin-gauge-sub">
        Excess Liquidity ÷ Net Liquidation — how far from a margin call
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Horizontal utilization bar with coloured zones + a marker for the
 * current value. Used for both "margin used" and "leverage".
 * ------------------------------------------------------------------ */
function UtilizationBar({ label, valueLabel, band, pctPosition, zoneStops, tooltip }) {
  return (
    <div className="margin-bar-block">
      <div className="margin-bar-label-row">
        <span className="margin-bar-label">
          {label}
          {tooltip ? <InfoPopover>{tooltip}</InfoPopover> : null}
        </span>
        <span className={`margin-bar-value ${band}`}>{valueLabel}</span>
      </div>
      <div className="margin-bar-track">
        <div className="margin-bar-zones">
          {zoneStops.map((z, i) => (
            <div key={i} className="margin-bar-zone" style={{ width: `${z.width}%`, background: ZONE_COLOR[z.color], opacity: 0.25 }} />
          ))}
        </div>
        <div
          className="margin-bar-marker"
          style={{ left: `${Math.max(0, Math.min(100, pctPosition))}%`, background: ZONE_COLOR[band] }}
        />
      </div>
      <div className="margin-bar-status">{BAND_LABEL[band]}</div>
    </div>
  );
}

function AccountBlock({ row, history }) {
  const netLiq = Number(row.net_liquidation) || 0;
  const cushionPct = Number(row.cushion) * 100;
  const marginUsedPct = netLiq > 0 ? (Number(row.maintenance_margin) / netLiq) * 100 : 0;
  const leverage = netLiq > 0 ? Number(row.gross_position_value) / netLiq : 0;

  const mBand = marginUsedBand(marginUsedPct);
  const lBand = leverageBand(leverage);
  const stale = isStale(row.updated_at);

  const chartData = (history || [])
    .slice()
    .sort((a, b) => (a.report_date < b.report_date ? -1 : 1))
    .map((h) => ({ date: h.report_date, cushionPct: Number(h.cushion) * 100 }));

  return (
    <div className="margin-account-block">
      <div className="margin-account-header">
        {row.account_id}
        <span className={`margin-account-updated ${stale ? 'stale' : ''}`}>
          Last updated: {formatUpdatedAt(row.updated_at)}{stale ? ' — stale, check TWS connection' : ''}
        </span>
      </div>

      <div className="margin-risk-row">
        <div className="margin-risk-card margin-key-stats-card">
          <div className="margin-risk-card-title">Key Numbers</div>
          <div className="margin-key-stats">
            <div className="margin-key-stat primary">
              <span className="margin-key-stat-label">
                Net Liquidation
                <InfoPopover>Total portfolio value: cash + positions, marked to market. Every ratio on this page is measured against this number.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.net_liquidation, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Equity w/ Loan Value
                <InfoPopover>Cash + stock/options value, before any margin loan is subtracted. IBKR's base for computing margin requirements.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.equity_with_loan_value, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Total Cash Value
                <InfoPopover>Total cash balance across all currencies, converted to account currency.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.total_cash_value, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Buying Power
                <InfoPopover>Total buying power for new stock positions - typically a multiple of Available Funds under Reg T margin.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.buying_power, row.currency)}</span>
            </div>
          </div>
        </div>

        <div className="margin-risk-card">
          <div className="margin-risk-card-title">
            Cushion
            <InfoPopover align="right">
              IBKR's own margin-cushion ratio: Excess Liquidity ÷ Net Liquidation. 0% means you're
              exactly at your maintenance margin requirement; negative means a margin call is
              already active. Higher is safer.
            </InfoPopover>
          </div>
          <RiskGauge pctValue={cushionPct} />
        </div>

        <div className="margin-risk-card">
          <div className="margin-risk-card-title">Risk Indicators</div>
          <UtilizationBar
            label="Margin Used"
            valueLabel={formatPct(marginUsedPct)}
            band={mBand}
            pctPosition={Math.min(100, marginUsedPct)}
            zoneStops={[
              { width: BANDS.marginUsed.orange, color: 'green' },
              { width: BANDS.marginUsed.red - BANDS.marginUsed.orange, color: 'orange' },
              { width: 100 - BANDS.marginUsed.red, color: 'red' },
            ]}
            tooltip="Maintenance Margin ÷ Net Liquidation - how much of your account's value is tied up meeting margin requirements right now. Higher = less room before requirements alone eat your excess liquidity."
          />
          <UtilizationBar
            label="Leverage"
            valueLabel={formatX(leverage)}
            band={lBand}
            pctPosition={Math.min(100, (leverage / LEVERAGE_BAR_MAX) * 100)}
            zoneStops={[
              { width: (BANDS.leverage.orange / LEVERAGE_BAR_MAX) * 100, color: 'green' },
              { width: ((BANDS.leverage.red - BANDS.leverage.orange) / LEVERAGE_BAR_MAX) * 100, color: 'orange' },
              { width: 100 - (BANDS.leverage.red / LEVERAGE_BAR_MAX) * 100, color: 'red' },
            ]}
            tooltip="Gross Position Value ÷ Net Liquidation. 1.0x = fully cash-covered, no borrowing. Above 1.0x you're controlling more market exposure than your own capital, amplifying gains and losses (and margin risk) alike."
          />
        </div>

        <div className="margin-risk-card margin-key-stats-card">
          <div className="margin-risk-card-title">Margin &amp; Trading Power</div>
          <div className="margin-key-stats">
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Excess Liquidity
                <InfoPopover>Buffer above the maintenance margin requirement, in currency - the same buffer the Cushion gauge shows as a percentage. Hits zero and you're at the edge of a margin call.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.excess_liquidity, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Maintenance Margin
                <InfoPopover>Margin required to keep current positions open. Falling below this (Excess Liquidity below zero) triggers a margin call.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.maintenance_margin, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Initial Margin
                <InfoPopover>Margin that would be required to open all current positions from scratch today - usually higher than the maintenance requirement.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.initial_margin, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Available Funds
                <InfoPopover>Funds available to open new positions right now, after margin requirements.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.available_funds, row.currency)}</span>
            </div>
            <div className="margin-key-stat">
              <span className="margin-key-stat-label">
                Gross Position Value
                <InfoPopover>Market value of all open positions (long + short), before cash. Used to compute the Leverage indicator.</InfoPopover>
              </span>
              <span className="margin-key-stat-value">{formatCurrency(row.gross_position_value, row.currency)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="margin-history-card">
        <div className="margin-history-title">
          Cushion Trend
          <InfoPopover align="right">
            Cushion at the last snapshot of each day, going back as far as history has been
            recorded. Builds up over time - a short line just means tracking only started
            recently, not that anything is wrong.
          </InfoPopover>
        </div>
        {chartData.length >= 2 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <YAxis
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={(v) => `${Number(v).toFixed(2)}%`}
                domain={['dataMin - 10', 'dataMax + 10']}
              />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Cushion']} />
              <ReferenceLine y={BANDS.cushion.green} stroke="#059669" strokeDasharray="4 4" strokeOpacity={0.5} />
              <ReferenceLine y={BANDS.cushion.orange} stroke="#dc2626" strokeDasharray="4 4" strokeOpacity={0.5} />
              <Line type="monotone" dataKey="cushionPct" stroke="#667eea" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="margin-history-note">
            Not enough history yet to chart a trend - one point is recorded per day, so this
            fills in over the coming days.
          </div>
        )}
      </div>
    </div>
  );
}

function Margin() {
  const [rows, setRows] = useState([]);
  const [historyByAccount, setHistoryByAccount] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWhatMatters, setShowWhatMatters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null); // { ok: bool, message: string }

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/portfolio/live-account-summary/`, {
        params: { page_size: 'all' },
      });
      const data = response.data.results || response.data;
      const list = Array.isArray(data) ? data : [];
      setRows(list);

      const histories = await Promise.all(
        list.map((row) =>
          axios
            .get(`${API_URL}/portfolio/account-summary-history/`, {
              params: { account_id: row.account_id, page_size: 'all' },
            })
            .then((r) => [row.account_id, r.data.results || r.data])
            .catch(() => [row.account_id, []])
        )
      );
      setHistoryByAccount(Object.fromEntries(histories));
    } catch (err) {
      console.error('Error fetching live account summary:', err);
      setError('Kon margin data niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const anyStale = rows.some((row) => needsRefreshButton(row.updated_at));

  const handleRefreshNow = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API_URL}/portfolio/margin/refresh/`);
      setToast({ ok: response.data.ok, message: response.data.message });
      if (response.data.ok) {
        await fetchSummary();
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Refresh failed - unknown error.';
      setToast({ ok: false, message });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="equity-container">
      <div className="equity-header">
        <h1>Margin</h1>
      </div>

      <div className="margin-explainer-bar">
        <span>
          <strong>Cushion</strong>, margin usage and leverage below use a <strong>Moderate</strong> risk
          profile - room to use margin, flagged well before things get tight.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          {anyStale && (
            <button
              type="button"
              className="margin-refresh-btn"
              onClick={handleRefreshNow}
              disabled={refreshing}
            >
              {refreshing && <span className="margin-refresh-spinner" />}
              {refreshing ? 'Refreshing…' : 'Refresh Now'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowWhatMatters(true)}
            style={{
              background: '#eef2ff', border: 'none', borderRadius: '6px',
              padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 500, color: '#4f46e5', cursor: 'pointer',
            }}
          >
            What matters here?
          </button>
        </div>
      </div>
      {showWhatMatters && <WhatMattersModal onClose={() => setShowWhatMatters(false)} />}
      {toast && (
        <div className={`margin-toast ${toast.ok ? 'ok' : 'error'}`}>
          {toast.ok ? '✓ ' : '⚠ '}{toast.message}
        </div>
      )}

      {loading && <div className="equity-loading">Loading...</div>}
      {error && <div className="equity-error">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="equity-empty">
          No live account summary yet - it's written hourly by
          scripts/ibkr/snapshot_account_summary.py while a TWS connection is
          available. Check back after the next run.
        </div>
      )}

      {!loading && !error && rows.map((row) => (
        <AccountBlock key={row.id} row={row} history={historyByAccount[row.account_id]} />
      ))}
    </div>
  );
}

export default Margin;
